import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Mail, Phone, Globe, MapPin, Calendar, Building2, Sparkles,
  Send, Loader2, MessageSquare, Video, MessagesSquare, Users as UsersIcon,
  CircleDot, ArrowRight, Trash2, ExternalLink, History, FileText,
} from "lucide-react";
import { accessApi } from "@/access/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

/* Right-side lead drawer — now organized in 3 tabs so the surface loads fast
   and stays scannable even on leads with hundreds of timeline entries.

   Tabs (only the active tab paints):
     1. Overview    — contact + attribution + Build OS summary + their message
     2. Activity    — log-activity composer + chronological timeline
     3. Submission  — full structured form data the prospect submitted
*/

const STATUSES = [
  { id: "new",       label: "New",       color: "violet"  },
  { id: "contacted", label: "Contacted", color: "amber"   },
  { id: "qualified", label: "Qualified", color: "sky"     },
  { id: "resolved",  label: "Resolved",  color: "emerald" },
];

const COLOR_BG = {
  violet:  "bg-violet-500/15 text-violet-200 ring-violet-400/30",
  amber:   "bg-amber-500/15 text-amber-200 ring-amber-400/30",
  sky:     "bg-sky-500/15 text-sky-200 ring-sky-400/30",
  emerald: "bg-emerald-500/15 text-emerald-200 ring-emerald-400/30",
};

const ACTIVITY_TYPES = [
  { id: "note_email", label: "Email",          Icon: Mail,           color: "violet"  },
  { id: "note_video", label: "Video call",     Icon: Video,          color: "blue"    },
  { id: "note_text",  label: "Text / SMS",     Icon: MessagesSquare, color: "sky"     },
  { id: "note_meet",  label: "Meet in person", Icon: UsersIcon,      color: "emerald" },
  { id: "note_other", label: "Other",          Icon: MessageSquare,  color: "amber"   },
];

const ACT_COLOR = {
  violet:  { dot: "bg-violet-400",  icon: "bg-violet-500/15 text-violet-200" },
  blue:    { dot: "bg-blue-400",    icon: "bg-blue-500/15 text-blue-200" },
  sky:     { dot: "bg-sky-400",     icon: "bg-sky-500/15 text-sky-200" },
  emerald: { dot: "bg-emerald-400", icon: "bg-emerald-500/15 text-emerald-200" },
  amber:   { dot: "bg-amber-400",   icon: "bg-amber-500/15 text-amber-200" },
  rose:    { dot: "bg-rose-400",    icon: "bg-rose-500/15 text-rose-200" },
};

const composePlaceholder = (t) => ({
  note_email: "What did you write / send?",
  note_video: "Notes from the call — what was discussed, next steps…",
  note_text:  "What did you text / DM them?",
  note_meet:  "Notes from the in-person meet…",
  note_other: "What happened on this lead?",
}[t] || "Notes…");

export const LeadDrawer = ({ lead, onClose, onUpdate, onDelete }) => {
  const [composeType, setComposeType] = useState("note_email");
  const [composeBody, setComposeBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [tab, setTab] = useState("overview");

  // Reset tab whenever a different lead opens
  useEffect(() => { setTab("overview"); }, [lead?.id]);

  // Lock body scroll while drawer open + Escape closes
  useEffect(() => {
    if (!lead) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [lead, onClose]);

  if (!lead) return null;
  const d = lead.data || {};
  const a = lead.attribution || {};
  const acts = [...(lead.activities || [])].reverse(); // newest first

  const moveStatus = async (status) => {
    if (status === lead.status) return;
    setBusy(true);
    try {
      const { data } = await accessApi.updateSubmission(lead.id, { status });
      onUpdate(data);
    } finally {
      setBusy(false);
    }
  };

  const submitActivity = async (e) => {
    e.preventDefault();
    if (!composeBody.trim()) return;
    setAdding(true);
    try {
      const { data } = await accessApi.addActivity(lead.id, {
        type: composeType, body: composeBody.trim(),
      });
      onUpdate(data);
      setComposeBody("");
    } finally {
      setAdding(false);
    }
  };

  const removeActivity = async (aid) => {
    if (!window.confirm("Delete this activity entry?")) return;
    const { data } = await accessApi.deleteActivity(lead.id, aid);
    onUpdate(data);
  };

  const remove = async () => {
    if (!window.confirm("Delete this lead permanently? This cannot be undone.")) return;
    await onDelete(lead.id);
    onClose();
  };

  return (
    <AnimatePresence>
      {lead && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm"
          />

          {/* Drawer */}
          <motion.aside
            key="drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 26, stiffness: 240 }}
            data-testid="lead-drawer"
            className="fixed inset-y-0 right-0 z-[101] flex w-full max-w-[640px] flex-col bg-[#0a0a14] text-white shadow-2xl"
          >
            {/* Sticky header */}
            <header className="sticky top-0 z-10 border-b border-white/[0.08] bg-[#0a0a14]/95 px-5 py-4 backdrop-blur sm:px-7">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-white/40">{lead.form_type}</p>
                  <h2 className="mt-1 font-display text-2xl font-black leading-tight tracking-tight">
                    {d.name || d.email || "(anonymous)"}
                  </h2>
                  <p className="mt-1 text-[13px] text-white/55">
                    {d.clinic_name || "—"} · captured {new Date(lead.created_at).toLocaleString()}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  data-testid="lead-drawer-close"
                  aria-label="Close"
                  className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-full border border-white/15 bg-white/[0.04] text-white/70 transition-colors hover:bg-white/[0.1] hover:text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Status pill bar */}
              <div className="mt-5 flex flex-wrap gap-1.5">
                {STATUSES.map((s, i) => {
                  const active = s.id === lead.status;
                  const next = STATUSES[i + 1]?.id === lead.status;
                  return (
                    <button
                      key={s.id}
                      onClick={() => moveStatus(s.id)}
                      disabled={busy}
                      data-testid={`lead-drawer-status-${s.id}`}
                      className={`relative inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11.5px] font-bold uppercase tracking-[0.14em] ring-1 transition-all ${
                        active
                          ? `${COLOR_BG[s.color]} ring-inset`
                          : next
                          ? "bg-white/[0.06] text-white/80 ring-white/[0.1] hover:bg-white/[0.1]"
                          : "bg-transparent text-white/40 ring-white/[0.08] hover:bg-white/[0.04] hover:text-white/70"
                      }`}
                    >
                      <CircleDot className={`h-3 w-3 ${active ? "" : "opacity-50"}`} />
                      {s.label}
                      {i < STATUSES.length - 1 && (
                        <ArrowRight className="ml-1 h-3 w-3 opacity-30" />
                      )}
                    </button>
                  );
                })}
              </div>
            </header>

            {/* Tabs — only the active one paints */}
            <Tabs value={tab} onValueChange={setTab} className="flex flex-1 flex-col overflow-hidden">
              <TabsList
                data-testid="lead-drawer-tablist"
                className="mx-5 mt-4 grid h-auto grid-cols-3 gap-1 rounded-xl bg-white/[0.04] p-1 sm:mx-7"
              >
                <TabsTrigger
                  value="overview"
                  data-testid="lead-tab-overview"
                  className="h-9 rounded-lg text-[13.5px] font-semibold text-white/60 data-[state=active]:bg-white/[0.08] data-[state=active]:text-white data-[state=active]:shadow-none"
                >
                  Overview
                </TabsTrigger>
                <TabsTrigger
                  value="activity"
                  data-testid="lead-tab-activity"
                  className="h-9 rounded-lg text-[13.5px] font-semibold text-white/60 data-[state=active]:bg-white/[0.08] data-[state=active]:text-white data-[state=active]:shadow-none"
                >
                  Activity{acts.length ? ` · ${acts.length}` : ""}
                </TabsTrigger>
                <TabsTrigger
                  value="submission"
                  data-testid="lead-tab-submission"
                  className="h-9 rounded-lg text-[13.5px] font-semibold text-white/60 data-[state=active]:bg-white/[0.08] data-[state=active]:text-white data-[state=active]:shadow-none"
                >
                  Submission
                </TabsTrigger>
              </TabsList>

              {/* OVERVIEW */}
              <TabsContent
                value="overview"
                data-testid="lead-panel-overview"
                className="flex-1 overflow-y-auto px-5 py-5 sm:px-7"
              >
                <div className="grid gap-2 sm:grid-cols-2">
                  {d.email && (
                    <ContactPill Icon={Mail}  label="Email" href={`mailto:${d.email}`}>{d.email}</ContactPill>
                  )}
                  {d.phone && d.phone !== "—" && (
                    <ContactPill Icon={Phone} label="Phone" href={`tel:${d.phone}`}>{d.phone}</ContactPill>
                  )}
                  {d.clinic_name && (
                    <ContactPill Icon={Building2} label="Clinic">{d.clinic_name}</ContactPill>
                  )}
                  {d.clinic_type && (
                    <ContactPill Icon={Sparkles}  label="Type">{d.clinic_type}</ContactPill>
                  )}
                </div>

                {/* Build-OS summary */}
                {lead.form_type === "build_os" && (
                  <BuildOsSummary d={d} />
                )}

                {/* Their message */}
                {d.message && (
                  <section className="mt-5 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
                    <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/45">Their message</p>
                    <p className="mt-2 text-[14px] leading-relaxed text-white/90 whitespace-pre-wrap">{d.message}</p>
                  </section>
                )}

                {/* Attribution */}
                <AttributionSection a={a} />
              </TabsContent>

              {/* ACTIVITY */}
              <TabsContent
                value="activity"
                data-testid="lead-panel-activity"
                className="flex-1 overflow-y-auto px-5 py-5 sm:px-7"
              >
                {/* Composer */}
                <section className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4">
                  <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/55">Log activity</p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {ACTIVITY_TYPES.map((t) => {
                      const active = composeType === t.id;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setComposeType(t.id)}
                          data-testid={`lead-compose-type-${t.id}`}
                          className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12px] font-bold transition-all ${
                            active
                              ? `border-transparent ${ACT_COLOR[t.color].icon}`
                              : "border-white/[0.08] bg-white/[0.02] text-white/55 hover:bg-white/[0.06] hover:text-white"
                          }`}
                        >
                          <t.Icon className="h-3.5 w-3.5" />
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                  <form onSubmit={submitActivity} className="mt-3 flex flex-col gap-2">
                    <textarea
                      value={composeBody}
                      onChange={(e) => setComposeBody(e.target.value)}
                      rows={3}
                      placeholder={composePlaceholder(composeType)}
                      data-testid="lead-compose-body"
                      className="block w-full resize-none rounded-lg border border-white/[0.1] bg-white/[0.02] px-3 py-2.5 text-[13.5px] outline-none placeholder:text-white/30 focus:border-violet-400/60"
                    />
                    <div className="flex items-center justify-end">
                      <button
                        type="submit"
                        disabled={adding || !composeBody.trim()}
                        data-testid="lead-compose-submit"
                        className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-violet-600 to-fuchsia-600 px-4 py-2 text-[12.5px] font-bold text-white shadow-md transition-all hover:-translate-y-0.5 disabled:opacity-50"
                      >
                        {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                        Log activity
                      </button>
                    </div>
                  </form>
                </section>

                {/* Timeline */}
                <section className="mt-6">
                  <p className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-white/55">
                    <History className="h-3.5 w-3.5" /> Timeline · {acts.length} {acts.length === 1 ? "entry" : "entries"}
                  </p>
                  {acts.length === 0 ? (
                    <p className="mt-3 rounded-xl border border-dashed border-white/10 px-4 py-6 text-center text-[13px] text-white/45">
                      No activity yet. Log your first note above.
                    </p>
                  ) : (
                    <ol className="relative mt-4 space-y-3 pl-7 before:absolute before:left-3 before:top-2 before:bottom-2 before:w-px before:bg-white/[0.08]">
                      {acts.map((act) => (
                        <ActivityEntry key={act.id} act={act} onDelete={removeActivity} />
                      ))}
                      <li className="relative">
                        <span className="absolute -left-[18px] top-1.5 inline-flex h-3 w-3 items-center justify-center rounded-full bg-white/15 ring-2 ring-[#0a0a14]" />
                        <p className="text-[12.5px] font-bold text-white/55">Submission created</p>
                        <p className="text-[11px] text-white/35">{new Date(lead.created_at).toLocaleString()}</p>
                      </li>
                    </ol>
                  )}
                </section>
              </TabsContent>

              {/* SUBMISSION */}
              <TabsContent
                value="submission"
                data-testid="lead-panel-submission"
                className="flex-1 overflow-y-auto px-5 py-5 sm:px-7"
              >
                <SubmissionPanel d={d} formType={lead.form_type} />
              </TabsContent>
            </Tabs>

            {/* Footer */}
            <footer className="border-t border-white/[0.08] bg-white/[0.02] px-5 py-3 sm:px-7">
              <div className="flex items-center justify-between">
                <a
                  href={a.page || "/"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[12px] font-bold text-white/55 hover:text-white"
                >
                  <ExternalLink className="h-3 w-3" /> Open source page
                </a>
                <button
                  type="button"
                  onClick={remove}
                  data-testid="lead-drawer-delete"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-rose-500/25 bg-rose-500/10 px-3 py-1.5 text-[12px] font-bold text-rose-200 hover:bg-rose-500/20"
                >
                  <Trash2 className="h-3 w-3" /> Delete lead
                </button>
              </div>
            </footer>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

/* ── helper subcomponents ─────────────────────────────────────── */

const BuildOsSummary = ({ d }) => (
  <section className="mt-5 rounded-2xl border border-blue-500/25 bg-gradient-to-br from-blue-500/[0.08] to-fuchsia-500/[0.05] p-4">
    <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-blue-300">Selected plan</p>
    <p className="mt-2 font-display text-lg font-black tracking-tight">
      {d.selected_base || "—"}
      {typeof d.selected_base_price === "number" && (
        <span className="ml-2 text-white/60 font-medium">· ${d.selected_base_price}/mo</span>
      )}
    </p>
    {Array.isArray(d.selected_addons) && d.selected_addons.length > 0 && (
      <p className="mt-2 text-[13.5px] text-white/85">
        <span className="text-white/45">Add-ons: </span>
        {d.selected_addons.join(", ")}
      </p>
    )}
    <div className="mt-3 flex items-center gap-3">
      {d.whitelabel && (
        <span className="inline-flex items-center rounded bg-fuchsia-500/20 px-2 py-1 text-[10.5px] font-bold uppercase tracking-[0.14em] text-fuchsia-200">
          Whitelabel
        </span>
      )}
      {typeof d.total_monthly === "number" && (
        <span className="font-display text-[20px] font-black tracking-tight">${d.total_monthly}<span className="text-[12.5px] font-bold text-white/55">/mo</span></span>
      )}
    </div>
  </section>
);

const AttributionSection = ({ a }) => {
  const utm = [a.utm_source, a.utm_medium, a.utm_campaign].filter(Boolean).join(" / ");
  const loc = [a.city, a.region, a.country].filter(Boolean).join(", ") || a.country;
  const items = [
    { Icon: Globe,    label: "Came from",    value: a.page,          mono: true },
    { Icon: Calendar, label: "Landing page", value: a.landing_page,  mono: true },
    { Icon: Sparkles, label: "UTM source",   value: utm },
    { Icon: MapPin,   label: "Location",     value: loc },
  ].filter((it) => it.value);
  if (items.length === 0) return null;
  return (
    <section className="mt-5">
      <p className="px-1 pb-2 text-[11px] font-bold uppercase tracking-[0.22em] text-white/45">Attribution</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {items.map((it) => <Attr key={it.label} {...it} />)}
      </div>
    </section>
  );
};

const SubmissionPanel = ({ d, formType }) => {
  const fields = Object.entries(d || {}).filter(([k, v]) => {
    if (v === null || v === undefined || v === "") return false;
    if (Array.isArray(v) && v.length === 0) return false;
    return true;
  });
  if (fields.length === 0) {
    return (
      <p className="rounded-xl border border-dashed border-white/10 px-4 py-8 text-center text-[13px] text-white/45">
        No submission data attached to this lead.
      </p>
    );
  }
  return (
    <div className="space-y-4">
      <p className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-white/55">
        <FileText className="h-3.5 w-3.5" />
        Submitted data · {formType}
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        {fields.map(([k, v]) => (
          <SubmissionField key={k} k={k} v={v} />
        ))}
      </div>
    </div>
  );
};

const SubmissionField = ({ k, v }) => {
  let display;
  if (typeof v === "boolean") display = v ? "Yes" : "No";
  else if (Array.isArray(v)) display = v.join(", ");
  else if (typeof v === "object") display = JSON.stringify(v);
  else if (typeof v === "number") display = String(v);
  else display = String(v);

  const isLong = typeof display === "string" && display.length > 80;
  return (
    <div className={`rounded-xl border border-white/[0.06] bg-white/[0.02] p-3.5 ${isLong ? "sm:col-span-2" : ""}`}>
      <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-white/45">
        {k.replace(/_/g, " ")}
      </p>
      <p className="mt-1.5 break-words text-[14px] font-semibold leading-relaxed text-white/90">
        {display}
      </p>
    </div>
  );
};

const ContactPill = ({ Icon, label, children, href }) => {
  const inner = (
    <span className="flex items-center gap-2.5 rounded-xl border border-white/[0.08] bg-white/[0.03] px-3.5 py-2.5">
      <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-white/[0.05] text-white/70">
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[10.5px] font-bold uppercase tracking-[0.18em] text-white/45">{label}</span>
        <span className="block truncate text-[14px] font-semibold text-white/90">{children}</span>
      </span>
    </span>
  );
  return href ? (
    <a href={href} className="block transition-colors hover:bg-white/[0.04] rounded-xl">{inner}</a>
  ) : inner;
};

const Attr = ({ Icon, label, value, mono }) => (
  <div className="flex items-start gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3.5">
    <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-white/[0.05] text-white/70">
      <Icon className="h-4 w-4" />
    </span>
    <span className="min-w-0 flex-1">
      <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-white/45">{label}</p>
      <p className={`mt-1 truncate text-[13.5px] font-semibold text-white/90 ${mono ? "font-mono" : ""}`}>
        {value || <span className="text-white/35">—</span>}
      </p>
    </span>
  </div>
);

const ActivityEntry = ({ act, onDelete }) => {
  const meta = ACTIVITY_TYPES.find((t) => t.id === act.type) || ACTIVITY_TYPES[4];
  const tone = ACT_COLOR[meta.color];
  const Icon = meta.Icon;
  return (
    <li className="relative">
      <span className={`absolute -left-[19px] top-1 inline-flex h-5 w-5 items-center justify-center rounded-full ring-2 ring-[#0a0a14] ${tone.icon}`}>
        <Icon className="h-2.5 w-2.5" />
      </span>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-white/55">
            {meta.label} · {new Date(act.at).toLocaleString()}
            {act.by && <span className="text-white/35"> · {act.by}</span>}
          </p>
          <p className="mt-1 whitespace-pre-wrap text-[13.5px] leading-relaxed text-white/85">{act.body}</p>
        </div>
        <button
          type="button"
          onClick={() => onDelete(act.id)}
          aria-label="Delete entry"
          className="inline-flex h-7 w-7 flex-none items-center justify-center rounded-md text-white/35 hover:bg-white/[0.05] hover:text-rose-200"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </li>
  );
};

export default LeadDrawer;
